import { FC, useEffect, useRef, useState } from "react";
import useStyles from "../../../lead/quote/leadEstimateStyle";
import {
  DeleteOutline,
  PhotoCamera,
  PlaylistAdd,
  VisibilityOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import closeIcon from "../../../../../assets/closeModel.png";
import InvJobDetailsInfo from "./InvJobDetailsInfo";
import InvSurveyItem from "./InvSurveyItem";
import AlertPopup from "../../../../../components/common/alertPopup/AlertPopup";
import FormField from "../../../../../components/common/FormField";
import CrewInventoryNoteModal from "../../../../../crewAdmin/components/CrewInventoryNoteModal";
import {
  getCrewSurveyItemsAction,
  getInventoryCrewDetailsAction,
  setInventoryNoteUpdatedAction,
  setUpdateInventoryCrewDetailsAction,
  uploadCrewInventoryPhotoAction,
  updateInventoryCrewDetailsAction,
} from "../../../../../store/reducers/inventoryCrewSlice";
import {
  CrewInventoryFormData,
  CrewInventoryItemFormData,
  CrewInventoryRequiredField,
  InventoryPayloadItem,
  crewItemFormFields,
  crewSignatureFields,
  initialCrewInventoryFormData,
  initialItemFormData,
} from "./utils";
import Notification from "../../../../../components/common/notification/Notification";
import { setNotificationAction } from "../../../../../store/reducers/mmymSlice";

interface ManageInventoryFormProps {
  show: boolean;
  setShow: (show: boolean) => void;
  screenType: string;
  readOnly?: boolean;
}

const ManageInventoryForm: FC<ManageInventoryFormProps> = ({
  show,
  setShow,
  readOnly = false,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const {
    clientInventoryCrewDetails,
    isInventoryCrewDetailsUpdated,
    isInventoryNoteUpdated,
    crewInventoryPhotoUpload,
    crewSurveyItems,
    isCrewSurveyItemsLoading,
  } = useSelector((state: any) => state.inventoryCrew);
  const [activeTab, setActiveTab] = useState<"capture" | "verification">(
    "capture",
  );
  const [inventoryFormData, setInventoryFormData] =
    useState<CrewInventoryFormData>(initialCrewInventoryFormData);
  const [itemFormData, setItemFormData] =
    useState<CrewInventoryItemFormData>(initialItemFormData);
  const [itemPhoto, setItemPhoto] = useState<File | null>(null);
  const [photoUrlByKey, setPhotoUrlByKey] = useState<Record<string, string>>(
    {},
  );
  const [editingItem, setEditingItem] = useState<InventoryPayloadItem | null>(
    null,
  );
  const [captureDirty, setCaptureDirty] = useState(false);
  const [formErrors, setFormErrors] = useState<
    Partial<Record<CrewInventoryRequiredField, string>>
  >({});
  const [pendingVerificationItems, setPendingVerificationItems] = useState<
    InventoryPayloadItem[]
  >([]);
  const [preloadRequested, setPreloadRequested] = useState(false);
  const [selectedVerificationItem, setSelectedVerificationItem] =
    useState<InventoryPayloadItem | null>(null);
  const [tabSwitchTarget, setTabSwitchTarget] = useState<
    "capture" | "verification" | null
  >(null);
  const clearFieldsAfterRefresh = useRef(false);
  const preserveFieldsAfterRefresh = useRef(false);
  const inventory = clientInventoryCrewDetails?.inventory || {};
  const contractId =
    clientInventoryCrewDetails?.contract_id ?? inventory.contract_id;
  const inventoryId = inventory.id;
  const responseItems = Array.isArray(clientInventoryCrewDetails?.items)
    ? clientInventoryCrewDetails.items
    : [];

  const filterValidItems = (items: InventoryPayloadItem[]) =>
    items.filter((item) => item.title?.trim() && Number(item.quantity) > 0);

  const removeRefId = (items: InventoryPayloadItem[]) =>
  items.map(({ ref_id, ...item }) => item);

  const inventoryStatus = String(
    clientInventoryCrewDetails?.status ??
      inventory.status ??
      inventoryFormData.status ??
      "",
  ).toUpperCase();
  const isSubmitted =
    inventoryStatus === "SUBMITTED" || inventoryStatus === "COMPLETED";
  const isCompleted = inventoryStatus === "COMPLETED";
  const inventoryItems: InventoryPayloadItem[] = responseItems.map(
    (item: any) => ({
      id: item.id ? String(item.id) : undefined,
      survey_item_id: item.survey_item_id ?? null,
      title: item.title ?? item.item ?? "",
      quantity: Number(item.quantity ?? item.qty ?? 0),
      condition_code: item.condition_code ?? item.condition ?? "",
      photo_key: item.photo_key ?? "",
      status: item.status ?? "PACKED",
      notes: item.notes ?? "",
    }),
  );
  const formatAddress = (address: any) => {
    if (typeof address === "string") {
      return address || "—";
    }
    if (!Array.isArray(address) || address.length === 0) {
      return "—";
    }
    return address
      .flatMap((part) => [
        part?.street,
        part?.street_2,
        part?.city,
        part?.town,
        part?.county,
        part?.postcode,
        part?.country,
      ])
      .filter((value) => value !== null && value !== undefined && value !== "")
      .join(", ");
  };
  const jobDetails = {
    job_id: clientInventoryCrewDetails?.lead_id,
    contract_no: clientInventoryCrewDetails?.contract_no,
    inventory_title: inventory.title,
    status: inventory.status ?? clientInventoryCrewDetails?.status,
    customer: clientInventoryCrewDetails?.customer_name,
    crew_email: inventory.crew_email,
    origin: formatAddress(clientInventoryCrewDetails?.collection_address),
    destination: formatAddress(clientInventoryCrewDetails?.delivery_address),
    date: inventory.created_at
      ? new Date(inventory.created_at).toLocaleDateString("en-GB")
      : "—",
    tag: inventoryItems.length ? `1 - ${inventoryItems.length}` : "—",
  };

  useEffect(() => {
    const details = clientInventoryCrewDetails;
    if (!details) {
      setInventoryFormData(initialCrewInventoryFormData);
      setItemFormData(initialItemFormData);
      setEditingItem(null);
      return;
    }
    const inventoryDetails = details.inventory || {};
    const rawItems = Array.isArray(details.items) ? details.items : [];
    const hydratedItems: InventoryPayloadItem[] = rawItems.map((item: any) => ({
      id: item.id ? String(item.id) : undefined,
       survey_item_id: item.survey_item_id
    ? String(item.survey_item_id)
    : null,
      title: item.title ?? item.item ?? "",
      quantity: Number(item.quantity ?? item.qty ?? 0),
      condition_code: item.condition_code ?? item.condition ?? "",
      photo_key: item.photo_key ?? "",
      status: item.status ?? "PACKED",
      notes: item.notes ?? "",
    }));
    const shouldClearItemForm = clearFieldsAfterRefresh.current;
    const shouldPreserveForm = preserveFieldsAfterRefresh.current;
    setInventoryFormData((previous) => ({
      status:
        details.status ??
        inventoryDetails.status ??
        initialCrewInventoryFormData.status,
      customer_sign: shouldPreserveForm
        ? previous.customer_sign
        : (details.customer_sign ?? inventoryDetails.customer_sign ?? ""),
      driver_sign_pickup: shouldPreserveForm
        ? previous.driver_sign_pickup
        : (details.driver_sign_pickup ??
          inventoryDetails.driver_sign_pickup ??
          ""),
      driver_sign_delivery: shouldPreserveForm
        ? previous.driver_sign_delivery
        : (details.driver_sign_delivery ??
          inventoryDetails.driver_sign_delivery ??
          ""),
      items: hydratedItems,
    }));
    setPhotoUrlByKey((previous) => ({
      ...previous,
      ...Object.fromEntries(
        rawItems
          .filter((item: any) => item.photo_key && item.photo_url)
          .map((item: any) => [item.photo_key, item.photo_url]),
      ),
    }));
    if (shouldClearItemForm) {
      setEditingItem(null);
      setItemFormData((previous) => ({
        ...initialItemFormData,
        ref_id: String(hydratedItems.length + 1),
      }));
      setFormErrors({});
      clearFieldsAfterRefresh.current = false;
    } else if (!shouldPreserveForm) {
      setEditingItem(null);
      setItemFormData({
        ...initialItemFormData,
        ref_id: String(hydratedItems.length + 1),
      });
    }
    preserveFieldsAfterRefresh.current = false;
  }, [clientInventoryCrewDetails]);
  const classes = useStyles({
    show,
    load: false,
  });

  const getItemKey = (item: InventoryPayloadItem) =>
  item.survey_item_id
    ? `survey:${item.survey_item_id}`
    : item.id
      ? `inventory:${item.id}`
      : `${item.title}:${item.quantity}:${item.condition_code}`;

  const handleFieldChange = (event: any) => {
    const { id, name, value } = event.target;
    const fieldId = (id || name) as
      | keyof CrewInventoryItemFormData
      | keyof CrewInventoryFormData;
    setCaptureDirty(true);
    if (
      fieldId === "customer_sign" ||
      fieldId === "driver_sign_pickup" ||
      fieldId === "driver_sign_delivery"
    ) {
      setInventoryFormData((previous) => ({ ...previous, [fieldId]: value }));
    } else {
      setItemFormData((previous) => ({ ...previous, [fieldId]: value }));
    }
  };

  const handlePreloadSurveyItems = () => {
    if (inventoryId && !isCrewSurveyItemsLoading) {
      setPreloadRequested(true);
      dispatch(getCrewSurveyItemsAction({ inventoryId, role: "client" }));
    }
  };

 useEffect(() => {
  if (!preloadRequested || isCrewSurveyItemsLoading) return;

  if (!Array.isArray(crewSurveyItems) || crewSurveyItems.length === 0) {
    setPreloadRequested(false);
    return;
  }

  // Already saved survey_item_id values
  const existingSurveyIds = new Set(
    inventoryFormData.items
      .map((item) => item.survey_item_id)
      .filter((id): id is string => Boolean(id)),
  );

  const maxRefId = inventoryFormData.items.reduce((max, item) => {
    const refId = Number(item.ref_id);
    return Number.isFinite(refId) ? Math.max(max, refId) : max;
  }, inventoryFormData.items.length);

  const newItems = crewSurveyItems
    .filter((item: any) => item?.id)
    .filter((item: any) => !existingSurveyIds.has(String(item.id)))
    .map((item: any, index: number) => ({
      survey_item_id: String(item.id), // Survey API ID
      title: item.item ?? item.title ?? item.item_name ?? item.name ?? "",
      quantity: Number(item.quantity ?? item.qty ?? 1),
      condition_code: item.condition_code ?? item.condition ?? "PBO",
      photo_key: item.photo_key ?? "",
      status: item.status ?? "PACKED",
      notes: item.notes ?? "",
      ref_id: String(maxRefId + index + 1),
    }))
    .filter(
      (item: InventoryPayloadItem) =>
        item.title.trim() !== "" &&
        Number.isFinite(item.quantity) &&
        item.quantity > 0,
    );

  if (newItems.length > 0) {
    setCaptureDirty(true);

    setInventoryFormData((previous) => ({
      ...previous,
      items: [...previous.items, ...newItems],
    }));

    setItemFormData((previous) => ({
      ...previous,
      ref_id: String(maxRefId + newItems.length + 1),
    }));
  }

  setPreloadRequested(false);
}, [
  crewSurveyItems,
  inventoryFormData.items,
  isCrewSurveyItemsLoading,
  preloadRequested,
]);

  const handleUploadPhoto = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setCaptureDirty(true);
      setItemPhoto(file);
      setItemFormData((previous) => ({ ...previous, photo_key: "" }));
      dispatch(uploadCrewInventoryPhotoAction({ file }));
    }
    event.target.value = "";
  };

  useEffect(() => {
    if (
      crewInventoryPhotoUpload?.key &&
      !crewInventoryPhotoUpload.isUploading
    ) {
      setItemFormData((previous) => ({
        ...previous,
        photo_key: crewInventoryPhotoUpload.key,
      }));
      if (crewInventoryPhotoUpload.url) {
        setPhotoUrlByKey((previous) => ({
          ...previous,
          [crewInventoryPhotoUpload.key]: crewInventoryPhotoUpload.url,
        }));
      }
    }
  }, [
    crewInventoryPhotoUpload?.key,
    crewInventoryPhotoUpload?.isUploading,
    crewInventoryPhotoUpload?.url,
  ]);

  const photoUrl = (key: string) =>
    photoUrlByKey[key] || (/^https?:\/\//i.test(key) ? key : "");

  const handleRemovePhoto = () => {
    setItemPhoto(null);
    setCaptureDirty(true);
    setItemFormData((previous) => ({ ...previous, photo_key: "" }));
  };

  const currentItem = (): InventoryPayloadItem => ({
    ...(editingItem || {}),
    title: String(itemFormData.title || "").trim(),
    quantity: Number(itemFormData.quantity),
    condition_code: itemFormData.condition_code,
    photo_key: itemFormData.photo_key,
    status: editingItem?.status ?? "PACKED",
    notes: editingItem?.notes ?? "",
  });

  const handleSubmit = (status: "DRAFT" | "SUBMITTED") => {
    if (!contractId || !inventoryId) return;

    const title = String(itemFormData.title || "").trim();
    const quantity = Number(itemFormData.quantity);

    const hasValidTitle = (item: InventoryPayloadItem) =>
      typeof item?.title === "string" && item.title.trim() !== "";

    const validExistingItems = inventoryFormData.items.filter(hasValidTitle);
    const hasExistingItems = validExistingItems.length > 0;

    // Blank sirf title se decide hoga (quantity me default value hoti hai)
    const isItemFormBlank = !title;

    // Validation: list khaali ho, ya edit ho raha ho, ya user ne title bhara ho
    const shouldValidateItem =
      !hasExistingItems || Boolean(editingItem) || !isItemFormBlank;

    const nextErrors: Partial<Record<CrewInventoryRequiredField, string>> = {};

    if (shouldValidateItem) {
      if (!title) nextErrors.title = "Item name is required.";
      if (!Number.isFinite(quantity) || quantity < 1) {
        nextErrors.quantity = "Quantity must be at least 1.";
      }
    }

    if (
      status === "SUBMITTED" &&
      !inventoryFormData.driver_sign_pickup.trim()
    ) {
      nextErrors.driver_sign_pickup = "Driver sign is required to submit.";
    }

    setFormErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    // Form blank hai aur edit nahi ho raha → current form ka item add mat karo
    const shouldSkipCurrentItem = isItemFormBlank && !editingItem;

    const nextItems = shouldSkipCurrentItem
      ? validExistingItems
      : editingItem
        ? inventoryFormData.items.map((item) =>
            getItemKey(item) === getItemKey(editingItem) ? currentItem() : item,
          )
        : [...inventoryFormData.items, currentItem()];

    // Remove items with quantity 0
    const filteredItems = filterValidItems(nextItems);

    dispatch(
      updateInventoryCrewDetailsAction({
        inventoryId,
        contractId,
        role: "client",
        inventoryData: {
          ...inventoryFormData,
          status,
          items: removeRefId(filteredItems),
        },
      }),
    );
  };

  const handleEditItem = (item: InventoryPayloadItem, index: number) => {
    setEditingItem(item);
    setCaptureDirty(false);
    setFormErrors({});
    setItemFormData({
      ref_id: String(responseItems[index]?.ref_id ?? index + 1),
      title: item.title,
      quantity: String(item.quantity),
      condition_code: item.condition_code,
      photo_key: item.photo_key,
    });
    setItemPhoto(null);
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setCaptureDirty(false);
    setItemFormData({
      ...initialItemFormData,
      ref_id: String(inventoryFormData.items.length + 1),
    });
    setItemPhoto(null);
  };

  const handleAddItem = () => {
    if (isSubmitted) return;
    setEditingItem(null);
    setCaptureDirty(false);
    setItemFormData({
      ...initialItemFormData,
      ref_id: String(inventoryFormData.items.length + 1),
    });
    setFormErrors({});
    setItemPhoto(null);
  };

  const handleTabChange = (nextTab: "capture" | "verification") => {
    if (nextTab === activeTab || (nextTab === "verification" && !isSubmitted)) {
      return;
    }
    const hasChanges =
      activeTab === "capture"
        ? captureDirty || Boolean(itemPhoto)
        : pendingVerificationItems.length > 0;
    if (hasChanges) {
      setTabSwitchTarget(nextTab);
      return;
    }
    setActiveTab(nextTab);
    if (contractId) {
      dispatch(setNotificationAction({ message: [], severity: "error" }));
      dispatch(getInventoryCrewDetailsAction({ contractId, role: "client" }));
    }
  };

  const handleDiscardAndSwitch = () => {
    if (!tabSwitchTarget) return;
    if (activeTab === "capture") {
      setEditingItem(null);
      setItemPhoto(null);
      setCaptureDirty(false);
      setFormErrors({});
      setInventoryFormData((previous) => ({
        ...previous,
        customer_sign:
          clientInventoryCrewDetails?.customer_sign ??
          inventory.customer_sign ??
          "",
        driver_sign_pickup:
          clientInventoryCrewDetails?.driver_sign_pickup ??
          inventory.driver_sign_pickup ??
          "",
        items: inventoryItems,
      }));
      setItemFormData({
        ...initialItemFormData,
        ref_id: String(inventoryItems.length + 1),
      });
    } else {
      setPendingVerificationItems([]);
      setSelectedVerificationItem(null);
      setInventoryFormData((previous) => ({
        ...previous,
        driver_sign_delivery:
          clientInventoryCrewDetails?.driver_sign_delivery ??
          inventory.driver_sign_delivery ??
          "",
      }));
    }
    setActiveTab(tabSwitchTarget);
    setTabSwitchTarget(null);
    if (contractId) {
      dispatch(getInventoryCrewDetailsAction({ contractId, role: "client" }));
    }
  };

  const handleVerificationSave = (values: {
    notes: string;
    driver_sign_delivery: string;
    status: string;
  }) => {
    if (isCompleted || !selectedVerificationItem) return;
    const updated = {
      ...selectedVerificationItem,
      notes: values.notes,
      status: values.status,
    };
    setPendingVerificationItems((previous) => [
      ...previous.filter((item) => getItemKey(item) !== getItemKey(updated)),
      updated,
    ]);
    setInventoryFormData((previous) => ({
      ...previous,
      driver_sign_delivery: values.driver_sign_delivery,
    }));
    setSelectedVerificationItem(null);
  };

  const handleVerificationItemClick = (item: InventoryPayloadItem) => {
    if (isCompleted) return;
    const pending = pendingVerificationItems.find(
      (value) => getItemKey(value) === getItemKey(item),
    );
    setSelectedVerificationItem(pending || item);
  };

  const handleSaveProgress = () => {
    if (
      !contractId ||
      !inventoryId ||
      isCompleted ||
      !pendingVerificationItems.length
    )
      return;
    const filteredItems = filterValidItems(pendingVerificationItems);

    dispatch(
      updateInventoryCrewDetailsAction({
        inventoryId,
        contractId,
        role: "client",
        isVerificationNoteUpdate: true,
        inventoryData: {
          ...inventoryFormData,
          status: "SUBMITTED",
          items: removeRefId(filteredItems),
        },
      }),
    );
  };

  const handleComplete = () => {
    if (!contractId || !inventoryId || !isSubmitted || isCompleted) return;
    const pendingByKey = new Map(
      pendingVerificationItems.map((item) => [getItemKey(item), item]),
    );
    const updatedItems = inventoryFormData.items.map(
      (item) => pendingByKey.get(getItemKey(item)) ?? item,
    );

    dispatch(
      updateInventoryCrewDetailsAction({
        inventoryId,
        contractId,
        role: "client",
        isVerificationNoteUpdate: true,
        inventoryData: {
          ...inventoryFormData,
          status: "COMPLETED",
          items: removeRefId(filterValidItems(updatedItems)),
        },
      }),
    );
  };

  useEffect(() => {
    if (isInventoryCrewDetailsUpdated) {
      clearFieldsAfterRefresh.current = true;
      setCaptureDirty(false);
      dispatch(setUpdateInventoryCrewDetailsAction(false));
      if (contractId) {
        dispatch(setNotificationAction({ message: [], severity: "error" }));
        dispatch(getInventoryCrewDetailsAction({ contractId, role: "client" }));
      }
    }
  }, [contractId, dispatch, isInventoryCrewDetailsUpdated]);

  useEffect(() => {
    if (isInventoryNoteUpdated) {
      preserveFieldsAfterRefresh.current = true;
      setPendingVerificationItems([]);
      setSelectedVerificationItem(null);
      dispatch(setInventoryNoteUpdatedAction(false));
      if (contractId) {
        dispatch(setNotificationAction({ message: [], severity: "error" }));
        dispatch(getInventoryCrewDetailsAction({ contractId, role: "client" }));
      }
    }
  }, [contractId, dispatch, isInventoryNoteUpdated]);

  const handleClose = () => {
    dispatch(setNotificationAction({ message: [], severity: "error" }));
    setShow(false);
  };

  const renderCapture = () => (
    <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
      <InvJobDetailsInfo data={jobDetails} />
      {!readOnly && (
        <Paper
          elevation={0}
          sx={{
            mt: 2,
            p: { xs: 1.5, md: 2 },
            border: "1px solid #e7edf1",
            borderRadius: 2,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={1}
            sx={{ mb: 1.5 }}
          >
            <Typography
              sx={{ fontSize: 12, fontWeight: 700, color: "#51656d" }}
            >
              {editingItem ? "Edit inventory item" : "New inventory item"}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                size="small"
                onClick={handleCancelEdit}
                sx={{ textTransform: "none" }}
              >
                {editingItem ? "Cancel edit" : "Clear form"}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  isCrewSurveyItemsLoading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <PlaylistAdd fontSize="small" />
                  )
                }
                disabled={
                  !inventoryId || isSubmitted || isCrewSurveyItemsLoading
                }
                onClick={handlePreloadSurveyItems}
                sx={{
                  textTransform: "none",
                  alignSelf: { xs: "flex-start", sm: "auto" },
                }}
              >
                {isCrewSurveyItemsLoading
                  ? "Loading survey items"
                  : "Preload survey items"}
              </Button>
            </Stack>
          </Stack>
          <Grid container spacing={1.5}>
            {crewItemFormFields.map((field) => (
              <FormField
                key={field.id}
                {...field}
                value={
                  itemFormData[field.id as keyof CrewInventoryItemFormData]
                }
                onChange={handleFieldChange}
                disabled={isSubmitted || field.disabled}
                error={Boolean(
                  formErrors[field.id as CrewInventoryRequiredField],
                )}
                // helperText={
                //   formErrors[field.id as CrewInventoryRequiredField] || ""
                // }
                isNonMarginTop
                inSingleGridForm
                InputProps={{ sx: { height: "36px" } }}
              />
            ))}
          </Grid>
          {/* {formErrors.title && (
          <Typography color="error" variant="caption">
            {formErrors.title}
          </Typography>
        )}
        {formErrors.quantity && (
          <Typography color="error" variant="caption">
            {formErrors.quantity}
          </Typography>
        )} */}
          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            {crewSignatureFields.map((field) => (
              <FormField
                key={field.id}
                {...field}
                value={
                  inventoryFormData[
                    field.id as "customer_sign" | "driver_sign_pickup"
                  ]
                }
                onChange={handleFieldChange}
                disabled={isSubmitted}
                error={Boolean(
                  formErrors[field.id as CrewInventoryRequiredField],
                )}
                isNonMarginTop
                inSingleGridForm
                InputProps={{ sx: { height: "36px" } }}
              />
            ))}
            {/* <FormField
              id="driver_sign_delivery"
              label="Driver sign at delivery"
              type="text"
              value={inventoryFormData.driver_sign_delivery}
              onChange={handleFieldChange}
              disabled={isSubmitted}
              isNonMarginTop
              inSingleGridForm
              md={6}
              InputProps={{ sx: { height: "36px" } }}
            /> */}
          </Grid>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mt: 1.5 }}
          >
            <Button
              component="label"
              variant="outlined"
              disabled={isSubmitted || crewInventoryPhotoUpload?.isUploading}
              startIcon={<PhotoCamera />}
              sx={{ textTransform: "none" }}
            >
              Add photo
              <input
                hidden
                type="file"
                accept="image/*"
                onChange={handleUploadPhoto}
              />
            </Button>
            {itemPhoto && (
              <Typography variant="caption">
                {crewInventoryPhotoUpload?.isUploading
                  ? `Uploading ${itemPhoto.name}...`
                  : itemFormData.photo_key
                    ? itemPhoto.name
                    : ""}
              </Typography>
            )}
            {itemFormData.photo_key && photoUrl(itemFormData.photo_key) && (
              <Button
                size="small"
                startIcon={<VisibilityOutlined />}
                onClick={() =>
                  window.open(
                    photoUrl(itemFormData.photo_key),
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
                sx={{ textTransform: "none" }}
              >
                View photo
              </Button>
            )}
            {itemFormData.photo_key && (
              <IconButton
                size="small"
                aria-label="Remove photo"
                disabled={isSubmitted}
                onClick={handleRemovePhoto}
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            )}
          </Stack>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            justifyContent="flex-end"
            sx={{ mt: 2 }}
          >
            <Button
              variant="outlined"
              disabled={isSubmitted || crewInventoryPhotoUpload?.isUploading}
              onClick={() => handleSubmit("DRAFT")}
              sx={{ textTransform: "none" }}
            >
              {editingItem ? "Update item" : "Save as draft"}
            </Button>
            <Button
              variant="contained"
              disabled={isSubmitted || crewInventoryPhotoUpload?.isUploading}
              onClick={() => handleSubmit("SUBMITTED")}
              sx={{ textTransform: "none" }}
            >
              Submit &amp; lock
            </Button>
          </Stack>
        </Paper>
      )}
      <Paper
        elevation={0}
        sx={{
          mt: 2,
          p: { xs: 1.5, md: 2 },
          border: "1px solid #e7edf1",
          borderRadius: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={1}
          sx={{ mb: 1.5 }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#51656d" }}>
            Inventory items
          </Typography>
        </Stack>
        <InvSurveyItem
          items={inventoryFormData.items
            .filter((item) => Number(item.quantity) > 0)
            .map((item) => ({
              ...item,
              photo_url: photoUrl(item.photo_key),
            }))}
          onEdit={handleEditItem}
          disableEdit={readOnly || isSubmitted}
        />
      </Paper>
    </Box>
  );

  const renderVerification = () => (
    <Box
      aria-disabled={isCompleted}
      sx={{
        p: { xs: 1.5, md: 2.5 },
        ...(isCompleted && { pointerEvents: "none" }),
      }}
    >
      <InvJobDetailsInfo data={jobDetails} />
      <Typography
        sx={{ fontSize: 12, fontWeight: 700, color: "#2e3d44", mt: 2, mb: 1 }}
      >
        Verification
      </Typography>
      <Grid container spacing={0.8} sx={{ mb: 1.5 }}>
        {inventoryFormData.items.map((item, index) => {
          const status =
            pendingVerificationItems.find(
              (pending) => getItemKey(pending) === getItemKey(item),
            )?.status ?? item.status;
          const isException = status === "exception";
          const isNotChecked = status === "not-checked" || status === "PACKED";
          return (
            <Grid item xs={3} key={item.id || index}>
              <ButtonBase
                type="button"
                onClick={() => handleVerificationItemClick(item)}
                disabled={readOnly}
                aria-label={`Edit notes for ${item.title}`}
                sx={{
                  width: "100%",
                  height: 76,
                  borderRadius: 1.5,
                  border: "1px solid #dfe8e2",
                  background: isException
                    ? "#f2d8a4"
                    : isNotChecked
                      ? "#f2f2f2"
                      : "#bfe0c7",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  color: isException
                    ? "#6f4d00"
                    : isNotChecked
                      ? "#8a8a8a"
                      : "#1f3a31",
                  px: 0.5,
                  fontWeight: 700,
                }}
              >
                <Typography
                  sx={{ fontSize: 18, lineHeight: 1.1, fontWeight: 700 }}
                >
                  {responseItems[index]?.ref_id ?? index + 1}
                </Typography>
                <Typography sx={{ fontSize: 11, lineHeight: 1.1, mt: 0.3 }}>
                  {item.title}
                </Typography>
              </ButtonBase>
            </Grid>
          );
        })}
      </Grid>
      {pendingVerificationItems.map((item, index) => (
        <Alert
          key={getItemKey(item)}
          severity={item.status === "exception" ? "warning" : "success"}
          sx={{ mb: 1 }}
        >
          <strong>
            Tag {responseItems[index]?.ref_id ?? index + 1}: {item.title}
          </strong>
          {item.notes ? ` — ${item.notes}` : " — No notes added."}
        </Alert>
      ))}
      {!readOnly && (
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            disabled={isCompleted || !pendingVerificationItems.length}
            onClick={handleSaveProgress}
            sx={{
              flex: 1,
              textTransform: "none",
              "&.Mui-disabled": { opacity: 1 },
            }}
          >
            Save progress
          </Button>
          <Button
            variant="contained"
            disabled={!isSubmitted || isCompleted || !inventoryId}
            onClick={handleComplete}
            sx={{
              flex: 1,
              textTransform: "none",
              "&.Mui-disabled": { opacity: 1 },
            }}
          >
            Complete + sign
          </Button>
        </Stack>
      )}
    </Box>
  );

  const formTitle = "Storage & Move Inventory";

  return (
    <>
      <Box
        id="estimate-popup-fixed"
        className={classes.estimatePopupContainer}
        sx={{
          p: 0,
          overflow: "auto",
          borderRadius: 2,
          border: "1px solid #dfe7ec",
        }}
        style={{ zIndex: show ? 11 : -1 }}
      >
        <Box sx={{ minHeight: "100%", background: "#f5f7fa" }}>
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: "#fff",
              px: { xs: 2, md: 3 },
              pt: 2,
              position: "sticky",
              top: 0,
              zIndex: 5,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {formTitle}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>
                  {activeTab === "capture"
                    ? "Inventory capture"
                    : "Verification"}
                </Typography>
              </Box>
              <IconButton
                aria-label="Close inventory"
                onClick={handleClose}
                sx={{ color: "#fff" }}
              >
                <img
                  src={closeIcon}
                  alt="Close"
                  style={{ width: 22, height: 22 }}
                />
              </IconButton>
            </Stack>
            <Tabs
              value={activeTab}
              onChange={(_, nextTab: "capture" | "verification") =>
                handleTabChange(nextTab)
              }
              variant="fullWidth"
              sx={{
                mt: 1,
                minHeight: 42,
                "& .MuiTabs-indicator": {
                  height: 3,
                  backgroundColor: "#a8e0cf",
                },
                "& .MuiTab-root": {
                  minHeight: 42,
                  color: "rgba(255,255,255,0.76)",
                  textTransform: "none",
                  fontWeight: 700,
                  "&.Mui-selected": {
                    color: "#fff",
                    background: "rgba(255,255,255,0.12)",
                  },
                  "&.Mui-disabled": { color: "rgba(255,255,255,0.38)" },
                },
              }}
            >
              <Tab label="Inventory capture" value="capture" />
              <Tab
                label="Verification"
                value="verification"
                disabled={!isSubmitted}
              />
            </Tabs>
          </Box>
          <Notification />
          {activeTab === "capture" ? renderCapture() : renderVerification()}
        </Box>
      </Box>
      <CrewInventoryNoteModal
        open={!readOnly && Boolean(selectedVerificationItem)}
        item={selectedVerificationItem}
        driverSignDelivery={inventoryFormData.driver_sign_delivery}
        onClose={() => setSelectedVerificationItem(null)}
        onSave={handleVerificationSave}
      />
      <AlertPopup
        open={Boolean(tabSwitchTarget)}
        title="Unsaved changes"
        content="Your changes on this tab have not been saved. Discard them and switch tabs?"
        yesText="Discard and switch"
        noText="Stay here"
        onHide={() => setTabSwitchTarget(null)}
        submitHandler={handleDiscardAndSwitch}
      />
    </>
  );
};
export default ManageInventoryForm;
